import React, { useState } from "react";

// Slice locali
import { FaseFrazioneReparto } from "./fasiFrazioniRepartoSlice";

// Utility
import { convertToDateTime } from "../../common/dateUtils/convertToDateTime";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// Icons
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import IconButton from "@mui/material/IconButton";

import { theme } from "../../theme";

// ============================================
// INTERFACES
// ============================================

interface FaseFrazioneRepartoDetailDialogProps {
    open: boolean;
    fase?: FaseFrazioneReparto;
    onClose: () => void;
}

// ============================================
// COMPONENT
// ============================================

const FaseFrazioneRepartoDetailDialog = ({
    open,
    fase,
    onClose,
}: FaseFrazioneRepartoDetailDialogProps) => {
    const [editMode, setEditMode] = useState(false);

    if (!fase) return null;

    const avanzamento = parseFloat(fase.percentuale_avanzamento || "0") || 0;

    const formatOre = (ore?: string) => {
        if (!ore) return "-";
        const num = parseFloat(ore);
        if (isNaN(num)) return "-";
        const h = Math.floor(num);
        const m = Math.round((num - h) * 60);
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };

    const handleClose = () => {
        setEditMode(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            // Fullscreen su mobile per tablet
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 1, sm: 2 },
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="h6" fontWeight="bold">
                            OdP {fase.numero_registrazione}
                        </Typography>
                        {fase.ha_ostacoli_attivi && (
                            <Chip
                                icon={<WarningAmberIcon />}
                                label="Ostacoli attivi"
                                color="warning"
                                size="small"
                            />
                        )}
                        {fase.is_bloccata && !fase.ha_ostacoli_attivi && (
                            <Chip label="Bloccata" color="error" size="small" />
                        )}
                    </Stack>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {/* Sezione: Info OdP */}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Articolo
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {fase.codice_articolo}
                                </Typography>
                            </Box>

                            <Box>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Cliente
                                    </Typography>
                                </Stack>
                                <Typography variant="body1">
                                    {fase.nome_cliente}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Lavorazione
                                </Typography>
                                <Typography variant="body1">
                                    {fase.descrizione}
                                </Typography>
                                {fase.codice_lavorazione && (
                                    <Typography variant="caption" color="text.secondary">
                                        Codice: {fase.codice_lavorazione}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Frazione
                                </Typography>
                                <Typography variant="body2">
                                    {fase.frazione_display}
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack spacing={2}>
                            {/* Quantità */}
                            <Box>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <InventoryIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Quantità
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Schedulata
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {fase.quantita} {fase.unita_misura}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Completata
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {fase.quantita_completata} {fase.unita_misura}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Ore */}
                            <Box>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Tempo
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Previsto
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatOre(fase.ore_previste)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Lavorato
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatOre(fase.ore_lavorate)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Residuo
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatOre(fase.ore_residue)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Date */}
                            <Box>
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary">
                                        Periodo schedulato
                                    </Typography>
                                </Stack>
                                <Typography variant="body1">
                                    {convertToDateTime(fase.data_inizio_pianificata)?.toFormat("dd/MM/yyyy")}
                                    {" — "}
                                    {convertToDateTime(fase.data_fine_pianificata)?.toFormat("dd/MM/yyyy")}
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Avanzamento */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Avanzamento
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                        <LinearProgress
                            variant="determinate"
                            value={avanzamento}
                            sx={{
                                flexGrow: 1,
                                height: 12,
                                borderRadius: 6,
                            }}
                            color={avanzamento >= 100 ? "success" : "primary"}
                        />
                        <Typography variant="body1" fontWeight="bold" sx={{ minWidth: 48 }}>
                            {avanzamento.toFixed(1)}%
                        </Typography>
                    </Stack>
                </Box>

                {/* Ostacoli */}
                {fase.ostacoli_pack && fase.ostacoli_pack.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Ostacoli attivi
                        </Typography>
                        <Stack spacing={1}>
                            {fase.ostacoli_pack.map((ostacolo) => (
                                <Card
                                    key={ostacolo.id}
                                    variant="outlined"
                                    sx={{
                                        borderColor: theme.palette.warning.main,
                                        backgroundColor: theme.palette.warning.light + "10",
                                    }}
                                >
                                    <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                                            <WarningAmberIcon fontSize="small" color="warning" />
                                            <Chip
                                                label={ostacolo.tipo_display}
                                                color="warning"
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Stack>
                                        <Typography variant="body2">
                                            {ostacolo.descrizione}
                                        </Typography>
                                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Segnalato: {convertToDateTime(ostacolo.data_segnalazione)?.toFormat("dd/MM/yyyy")}
                                            </Typography>
                                            {ostacolo.data_prevista_risoluzione && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Risoluzione prevista: {convertToDateTime(ostacolo.data_prevista_risoluzione)?.toFormat("dd/MM/yyyy")}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* Note */}
                {fase.note && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Note
                        </Typography>
                        <Typography variant="body2">
                            {fase.note}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {!editMode ? (
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditMode(true)}
                        disabled
                    >
                        Modifica
                    </Button>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        Modalità editing — in sviluppo
                    </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={handleClose}>
                    Chiudi
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FaseFrazioneRepartoDetailDialog;
